import LandingHeadline from "../common/LandingHeadline";
import { MessagesSquare, Globe, Gauge, BarChart3, Sparkles } from "lucide-react";
import Header from "../Header/Header";
import Footer from "../Footer/footer";
import SEOHead from "../SEO/SEOHead";
import { SectionHeading, ScreenshotFrame, Button } from "../common/ui";
import "../../css/MainPage.css";
import "../../css/Pages.css";
import "../../css/AIEvaluation.css";
import conversationShot from "../../../assets/product/ai-eval-create.webp";
import liveChatShot from "../../../assets/product/ai-eval-live-create.webp";

const capabilities = [
  {
    icon: MessagesSquare,
    title: "Conversation Evaluation",
    text: "Paste or upload conversations (CSV), or question-and-expected-answer sets, and score them in conversational or contextual mode.",
  },
  {
    icon: Globe,
    title: "Live Chat Evaluation",
    text: "Point Froth TestOps at your website's chatbot. It drives the chat in a real browser, captures each response with screenshots and scores it.",
  },
  {
    icon: Gauge,
    title: "17 quality metrics",
    text: "Answer relevancy, faithfulness, hallucination detection, context precision and recall, completeness, helpfulness, prompt adherence, bias, toxicity, safety compliance and more.",
  },
  {
    icon: BarChart3,
    title: "Reports",
    text: "Track total evaluations, pass rate, average score and your strongest and weakest metrics over time.",
  },
];

const metrics = [
  "Answer Relevancy",
  "Faithfulness",
  "Hallucination Detection",
  "Context Precision",
  "Context Recall",
  "Completeness",
  "Helpfulness",
  "Prompt Adherence",
  "Response Consistency",
  "Bias Detection",
  "Toxicity",
  "Safety Compliance",
  "Sentiment Analysis",
  "Conversation Completeness",
  "Knowledge Retention",
  "Role Adherence",
  "Turn Relevancy",
];

export default function AIEvaluation() {
  return (
    <>
      <SEOHead
        title="AI Chatbot & LLM Evaluation | Froth TestOps"
        description="Test your chatbots and LLM features with an LLM judge that scores relevancy, faithfulness, hallucination, toxicity and more."
        keywords="AI evaluation, LLM evaluation, chatbot testing, hallucination detection, LLM as a judge, Froth TestOps"
      />
      <div className="fd-main-wrapper page-padding">
        <Header />
        <main>
          <section className="ft-page-hero aie-hero">
            <div className="ft-container pg-hero-grid">
              <div>
                <span className="ft-eyebrow">
                  <Sparkles size={15} aria-hidden="true" /> AI Evaluation
                </span>
                <LandingHeadline className="ft-display">
                  Evaluate Your AI <span>Before Your Customers Do</span>
                </LandingHeadline>
                <p className="ft-lead">
                  If your product includes a chatbot or an AI assistant, it needs testing too. Froth TestOps AI
                  Evaluation uses an LLM judge to score every answer against quality and safety metrics, and gives you
                  a pass/fail result, a risk level and recommendations.
                </p>
                <div className="ft-btn-row">
                  <Button to="/contact" icon>
                    Talk to Us
                  </Button>
                  <Button to="/test-labs" variant="ghost">
                    See Test Labs
                  </Button>
                </div>
              </div>
              <div className="aie-hero__visual">
                <ScreenshotFrame src={conversationShot} alt="Create New Evaluation form with a sample conversation to evaluate" />
              </div>
            </div>
          </section>

          {/* Capabilities as an open spec ledger. */}
          <section className="ft-section">
            <div className="ft-container">
              <SectionHeading
                align="left"
                eyebrow="Capabilities"
                title={
                  <>
                    Two ways to test <span>your AI</span>
                  </>
                }
                subtitle="Evaluate recorded conversations, or let Froth TestOps talk to your live chatbot for you."
              />
              <div className="aie-ledger">
                {capabilities.map((c, i) => {
                  const Icon = c.icon;
                  return (
                    <div className="aie-ledger__row" key={c.title}>
                      <span className="aie-ledger__num" aria-hidden="true">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="aie-ledger__icon">
                        <Icon size={20} aria-hidden="true" />
                      </span>
                      <div>
                        <h3 className="aie-ledger__title">{c.title}</h3>
                        <p className="aie-ledger__text">{c.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Live chat: screenshot on a tilted gradient back-sheet. */}
          <section className="ft-section">
            <div className="ft-container">
              <div className="ft-split ft-split--reverse">
                <div>
                  <SectionHeading
                    align="left"
                    eyebrow="Live Chat Evaluation"
                    title="Test the chatbot your users actually see"
                    subtitle="Give Froth TestOps your chatbot's URL and the steps to open it, or import automation steps from a test case. It sends your test questions, captures every answer and scores it."
                  />
                </div>
                <div className="aie-live__visual">
                  <ScreenshotFrame
                    src={liveChatShot}
                    alt="Create New Live Chat Evaluation form with test steps, test data and evaluation metrics"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Verdict pipeline. */}
          <section className="ft-section">
            <div className="ft-container">
              <SectionHeading
                eyebrow="How it works"
                title={
                  <>
                    From conversation to <span>verdict</span>
                  </>
                }
              />
              <div className="aie-pipeline">
                <div className="aie-stage">
                  <span className="aie-stage__node" aria-hidden="true">
                    01
                  </span>
                  <h3 className="aie-stage__title">Add what to evaluate</h3>
                  <p className="aie-stage__text">
                    Paste or upload conversations, or point Froth TestOps at a live chatbot on your website.
                  </p>
                </div>
                <div className="aie-stage">
                  <span className="aie-stage__node" aria-hidden="true">
                    02
                  </span>
                  <h3 className="aie-stage__title">Let the judge score it</h3>
                  <p className="aie-stage__text">An LLM judge scores every answer against the metrics you choose.</p>
                </div>
                <div className="aie-stage">
                  <span className="aie-stage__node" aria-hidden="true">
                    03
                  </span>
                  <h3 className="aie-stage__title">Review and improve</h3>
                  <p className="aie-stage__text">
                    See the overall score, risk level, detailed findings and FrothAI recommendations, then re-run after
                    each change.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Metrics as a light report sheet on the dark band. */}
          <section className="ft-section ft-section--dark">
            <div className="ft-container">
              <SectionHeading eyebrow="Metrics" title="17 ways to measure an answer" />
              <div className="aie-sheet">
                <span className="aie-sheet__count" aria-hidden="true">
                  {metrics.length}
                </span>
                <div className="aie-sheet__grid">
                  {metrics.map((m, i) => (
                    <div className="aie-metric" key={m}>
                      <span className="aie-metric__id" aria-hidden="true">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {m}
                    </div>
                  ))}
                </div>
                <div className="aie-sheet__models">
                  <span>Choose your judge model from</span>
                  <span className="aie-sheet__model">OpenAI</span>
                  <span className="aie-sheet__model">Azure OpenAI</span>
                  <span className="aie-sheet__model">Anthropic</span>
                  <span className="aie-sheet__model">Gemini</span>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}
